import { useState } from 'react'

import { faCreditCard } from '@fortawesome/pro-regular-svg-icons'
import { faXmark } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { PayMethodIcon } from '@b-stock/fe-core/bstock-next/components'
import { Banner, Button, FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'
import type { PayMethod } from '@b-stock/payments-methods-api-client'

import {
  getDetails,
  isCreditCardMethod,
} from '@/buyer-portal/components/features/order/checkout/PaymentMethods/utils'

import { sellerCollectsCopyId, type SaleKind } from './sellerCollectsBidCopy'

const ns = 'BidConfirmationModal.sellerCollects'

const Section = styled.div`
  margin-top: 0.75rem;
`

const Heading = styled.p`
  ${Typography.Subtitle2}
  margin: 0 0 0.25rem;
`

const Note = styled.p`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};
  margin: 0;
`

/**
 * The card the fee would be charged to: the selected method where that is a
 * card, otherwise any other saved card.
 *
 * Balance charges the fee invoice to a card, so ACH and Net Terms are not
 * "on file" for this — but a buyer whose *default* is one of those may still
 * have a card saved, and telling them otherwise pushes them to add a second.
 */
export const findFeeCard = (
  paymentMethods: PayMethod[],
  selectedMethod?: PayMethod | null
): PayMethod | undefined => {
  if (selectedMethod && isCreditCardMethod(selectedMethod.type))
    return selectedMethod
  return paymentMethods.find((method) => isCreditCardMethod(method.type))
}

/** BCD-3's sunken row, shared by the saved card and the dismissed note. */
const SunkenRow = styled.div`
  align-items: center;
  background: ${Colors.Semantic.Neutral.light_grey};
  border-radius: 0.375rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: space-between;
  margin-top: 0.75rem;
  padding: 0.875rem 1rem;
`

const CardIdentity = styled.span`
  align-items: center;
  display: inline-flex;
  gap: 0.75rem;
  min-width: 0;
`

const CardLabel = styled.span`
  ${Typography.Subtitle3}
  color: ${Colors.Semantic.Neutral.black};
`

const DefaultChip = styled.span`
  ${Typography.Body3}
  align-items: center;
  background: ${Colors.Semantic.Neutral.white};
  border-radius: 0.1875rem;
  color: ${Colors.Semantic.Neutral.dark_grey};
  display: inline-flex;
  height: 1.5rem;
  padding: 0 0.625rem;
`

// The design's yellow prompt: the design system's warning banner, with the
// card icon and the labelled dismiss kept in its own head row rather than the
// banner's generic triangle and unlabelled ×.
const Prompt = styled(Banner)`
  margin-top: 0.75rem;
`

const PromptHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
`

const PromptCopy = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`

const PromptTitle = styled.span`
  ${Typography.Subtitle3}
  color: ${Colors.Semantic.Neutral.black};
`

const PromptIcon = styled(FontAwesomeIcon)`
  color: ${Colors.Semantic.Neutral.black};
  margin-top: 0.125rem;
`

const DismissButton = styled.button`
  background: none;
  border: 0;
  color: ${Colors.Semantic.Neutral.dark_grey};
  cursor: pointer;
  flex: 0 0 auto;
  height: 1.5rem;
  padding: 0;
  width: 1.5rem;

  &:hover {
    color: ${Colors.Semantic.Neutral.black};
  }
`

const PromptActions = styled.div`
  display: flex;
  margin-top: 0.875rem;
  padding-left: 1.5rem;
`

const LinkButton = styled.button`
  ${Typography.Subtitle3}
  background: none;
  border: none;
  color: ${Colors.Semantic.BStock.default.lightBackground};
  cursor: pointer;
  padding: 0;
  text-align: right;

  &:hover {
    text-decoration: underline;
  }

  &:focus {
    outline: 0.125rem solid ${Colors.Semantic.BStock.default.lightBackground};
    outline-offset: 0.125rem;
  }
`

type SellerCollectsFeeSectionProps = {
  /**
   * From the seller's subscription. Undefined while it loads — neither state
   * is claimed until known, since the two say opposite things about money.
   */
  hasBuyerPremium?: boolean
  /** Falls back to generic copy when the seller cannot be named. */
  sellerName?: string
  /**
   * The card the fee would be charged to, resolved by {@link findFeeCard}.
   * Only a card is one: Balance charges the fee invoice to a card, so a buyer
   * whose only methods are ACH or Net Terms has nothing on file for this and
   * sees the prompt instead.
   */
  savedMethod?: PayMethod | null
  /**
   * The account default, so BCD-3's chip marks the card actually shown. The
   * caller cannot compare it against the selection: where the selection is
   * ACH or Net Terms, the card below is a fallback and is not the default.
   */
  defaultMethodId?: string | null
  /** Buy Now has no winning, so the prompt counts from the purchase. */
  saleKind?: SaleKind
  onPaymentMethodClick: () => void
  /**
   * "Add a card": opens the credit card form directly rather than the picker
   * (FP-3784). Falls back to `onPaymentMethodClick` where a caller has none.
   */
  onAddCardClick?: () => void
  /**
   * False when the account blocks credit cards: there is nothing to add, and
   * Balance emails the fee invoice instead.
   */
  canAddCard?: boolean
  /**
   * False where the picker behind "Change" would not be card-only: the saved
   * card is shown with nothing to open.
   */
  canChangeCard?: boolean
}

/**
 * What B-Stock will charge on a Seller Collects bid, and what it will not.
 *
 * Replaces the AutoPay selector, which FP-1182 suppressed here outright: the
 * standard framing reads as "this is how I pay for the lot", when the method
 * can only ever be charged the B-Stock fee. B-Stock AutoPay genuinely does not
 * run on these orders — `auto-charge-check.service.ts` excludes them — so the
 * countdown and auto-charge copy stay suppressed; only the fee framing returns.
 *
 * BCD-3 gives the fee-bearing case three states: the saved card, the prompt to
 * save one, and what the buyer sees after dismissing that prompt. A fee-free
 * storefront has no fee to charge and so has none of them.
 */
export const SellerCollectsFeeSection = ({
  hasBuyerPremium,
  sellerName,
  savedMethod,
  defaultMethodId,
  saleKind = 'bid',
  onPaymentMethodClick,
  onAddCardClick = onPaymentMethodClick,
  canAddCard = true,
  canChangeCard = true,
}: SellerCollectsFeeSectionProps) => {
  const { formatMessage } = useIntl()
  // Dismissal lives for this opening of the modal only: the prompt is advice,
  // and a buyer who closes it has not changed anything about the account.
  const [isPromptDismissed, setIsPromptDismissed] = useState(false)
  // Callers resolve this with `findFeeCard`; the guard stays so a caller
  // that passes a raw selection cannot render ACH as a card on file.
  const savedCard =
    savedMethod && isCreditCardMethod(savedMethod.type)
      ? savedMethod
      : undefined
  // The brand and last four live under a per-type `details` shape, which this
  // helper already unwraps for every method the picker can return.
  const savedCardDetails = savedCard ? getDetails(savedCard) : undefined

  if (hasBuyerPremium === undefined) return null

  if (!hasBuyerPremium) {
    return (
      <Section data-testid="seller-collects-no-fee">
        <Heading>
          <FormattedMessage id={`${ns}.headingNoFee`} />
        </Heading>
        <Note>
          {sellerName ? (
            <FormattedMessage id={`${ns}.noFee`} values={{ sellerName }} />
          ) : (
            <FormattedMessage id={`${ns}.noFeeFallback`} />
          )}
        </Note>
      </Section>
    )
  }

  return (
    <Section data-testid="seller-collects-fee-method">
      <Heading>
        <FormattedMessage id={`${ns}.heading`} />
      </Heading>
      <Note>
        <FormattedMessage id={`${ns}.feeOnlyNote`} />
      </Note>

      {savedCard ? (
        <SunkenRow data-testid="seller-collects-saved-card">
          <CardIdentity>
            <PayMethodIcon
              payMethodName={savedCardDetails?.brand || savedCard.type}
            />
            <CardLabel>
              <FormattedMessage
                id={`${ns}.cardEndingIn`}
                values={{ last4: savedCardDetails?.last4 ?? '' }}
              />
            </CardLabel>
            {savedCard.id === defaultMethodId && (
              <DefaultChip data-testid="seller-collects-card-default">
                <FormattedMessage id={`${ns}.defaultMethod`} />
              </DefaultChip>
            )}
          </CardIdentity>
          {canChangeCard && (
            <Button
              type="button"
              size="small"
              appearance="secondary"
              onClick={onPaymentMethodClick}
              data-testid="seller-collects-change-card"
            >
              <FormattedMessage id={`${ns}.changeCard`} />
            </Button>
          )}
        </SunkenRow>
      ) : isPromptDismissed ? (
        <SunkenRow data-testid="seller-collects-card-skipped">
          <Note>
            <FormattedMessage id={`${ns}.cardSkipped`} />
          </Note>
          {canAddCard && (
            <LinkButton
              type="button"
              onClick={onAddCardClick}
              data-testid="seller-collects-add-card-instead"
            >
              <FormattedMessage id={`${ns}.addCardInstead`} />
            </LinkButton>
          )}
        </SunkenRow>
      ) : (
        <Prompt type="warning" hideIcon narrow>
          <div data-testid="seller-collects-card-prompt">
            <PromptHead>
              <PromptIcon icon={faCreditCard} aria-hidden="true" />
              <PromptCopy>
                <PromptTitle>
                  <FormattedMessage id={`${ns}.cardPromptTitle`} />
                </PromptTitle>
                <Note>
                  <FormattedMessage
                    id={
                      // An account that blocks cards cannot save one, so
                      // only say that Balance emails the invoice — as the
                      // picker's card-blocked state does.
                      canAddCard
                        ? sellerCollectsCopyId('cardPromptBody', saleKind)
                        : `${ns}.cardSkipped`
                    }
                  />
                </Note>
              </PromptCopy>
              <DismissButton
                type="button"
                onClick={() => setIsPromptDismissed(true)}
                aria-label={formatMessage({ id: `${ns}.cardPromptDismiss` })}
                data-testid="seller-collects-card-prompt-dismiss"
              >
                <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
              </DismissButton>
            </PromptHead>
            {canAddCard && (
              <PromptActions>
                <Button
                  type="button"
                  size="small"
                  onClick={onAddCardClick}
                  data-testid="seller-collects-add-card"
                >
                  <FormattedMessage id={`${ns}.addCard`} />
                </Button>
              </PromptActions>
            )}
          </div>
        </Prompt>
      )}
    </Section>
  )
}
